import React, { useEffect, useState } from 'react';
import { isConnectorAvailable } from '../config/serverConfig';

const FEATURES = [
  { icon: '🗄', label: 'NAS & Network Storage', desc: 'Browse and play music from your local network drives' },
  { icon: '🔊', label: 'Network Players', desc: 'Stream to UPnP, DLNA, and Roon-ready players on your LAN' },
  { icon: '🎛', label: 'DSP & Room Correction', desc: 'Apply parametric EQ and room correction filters locally' },
  { icon: '🎧', label: 'Unrestricted Audio Devices', desc: 'Route audio to any connected output — DACs, interfaces, multichannel' },
];

const UPDATE_BASE = 'https://api.maestroaudio.net/api/maestro/updates/download';
const MAC_DOWNLOAD_URL = `${UPDATE_BASE}/Maestro_aarch64.dmg`;
const WIN_DOWNLOAD_URL = `${UPDATE_BASE}/Maestro_x64-setup.exe`;

export default function FullFeaturedModal({ isOpen, onClose }) {
  const [connectorActive, setConnectorActive] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setConnectorActive(isConnectorAvailable());
    const handleKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const connectorInfo = connectorActive ? window.__MAESTRO_CONNECTOR__ : null;

  return (
    <div className="fixed inset-0 z-[90] flex items-end sm:items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />

      <div
        className="relative w-full max-w-lg rounded-3xl shadow-2xl overflow-hidden"
        style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', color: 'var(--text-primary)' }}
      >
        {/* Header */}
        <div className="px-6 pt-6 pb-4 flex items-start justify-between gap-4">
          <div>
            <h2 className="text-xl font-extrabold">Maestro Connector</h2>
            <p className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>
              {connectorActive
                ? 'Maestro Connector is active — local network features are enabled.'
                : 'Extend Maestro with local network access using the Maestro Connector.'}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex items-center justify-center rounded-xl transition touch-target flex-shrink-0"
            style={{ width: 44, height: 44, background: 'var(--bg-secondary)', border: '1px solid var(--border)', color: 'var(--text-primary)' }}
            title="Close"
          >
            ×
          </button>
        </div>

        {/* Connected badge */}
        {connectorActive && (
          <div className="mx-6 mb-3 px-4 py-2.5 rounded-2xl flex items-center gap-2"
            style={{ background: 'rgba(201, 168, 76, 0.12)', border: '1px solid rgba(201, 168, 76, 0.3)' }}>
            <span className="w-2.5 h-2.5 rounded-full bg-green-400 animate-pulse flex-shrink-0" />
            <span className="text-sm font-semibold" style={{ color: '#C9A84C' }}>
              Connected{connectorInfo?.version ? ` — v${connectorInfo.version}` : ''}
            </span>
          </div>
        )}

        {/* Feature list */}
        <div className="px-6 pb-4 space-y-3">
          {FEATURES.map((f) => (
            <div key={f.label} className="flex items-start gap-3">
              <span className="text-lg mt-0.5 flex-shrink-0">{f.icon}</span>
              <div>
                <div className="text-sm font-bold">{f.label}</div>
                <div className="text-xs" style={{ color: 'var(--text-secondary)' }}>{f.desc}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Connector explanation */}
        {!connectorActive && (
          <div className="mx-6 mb-3 p-4 rounded-2xl" style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border)' }}>
            <p className="text-xs leading-relaxed" style={{ color: 'var(--text-secondary)' }}>
              The Maestro Connector is a small desktop app that runs quietly in the background.
              It connects Maestro to local hardware — NAS drives, DACs, and network players on your home network.
            </p>
          </div>
        )}

        {/* Subscription note */}
        <div className="mx-6 mb-4 flex items-center gap-2">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#C9A84C" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="flex-shrink-0">
            <polyline points="20 6 9 17 4 12" />
          </svg>
          <p className="text-xs font-medium" style={{ color: 'var(--text-secondary)' }}>
            Included with your Maestro subscription — no extra charge.
          </p>
        </div>

        {/* Download buttons (hide when already connected) */}
        {!connectorActive && (
          <div className="px-6 pb-6 flex flex-col sm:flex-row gap-3">
            <a
              href={MAC_DOWNLOAD_URL}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-2xl text-sm font-bold transition"
              style={{ background: '#C9A84C', color: '#1a1a2e' }}
            >
              <span>⬇</span>
              Download for Mac
            </a>
            <a
              href={WIN_DOWNLOAD_URL}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-2xl text-sm font-bold transition"
              style={{
                background: 'var(--bg-secondary)',
                border: '1px solid var(--border)',
                color: 'var(--text-primary)',
                textDecoration: 'none',
              }}
            >
              <span>⬇</span>
              Download for Windows
            </a>
          </div>
        )}

        {/* Close button when connected */}
        {connectorActive && (
          <div className="px-6 pb-6">
            <button
              type="button"
              onClick={onClose}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-2xl text-sm font-bold transition"
              style={{ background: '#C9A84C', color: '#1a1a2e' }}
            >
              Got It
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
